import './VerifiedSeal.css';

interface VerifiedSealProps {
  size?: number;
  className?: string;
}

/**
 * BluePages verification seal — embossed certificate stamp style.
 * Clean concentric rings with curved text, monochrome blue, print-safe.
 */
export function VerifiedSeal({ size = 44, className = '' }: VerifiedSealProps) {
  const cx = size / 2;
  const cy = size / 2;

  // Radii
  const outerR = size / 2 - 1.5;
  const ringR = outerR - size * 0.055;
  const textR = outerR * 0.72;
  const innerR = outerR * 0.42;
  const checkR = outerR * 0.24;

  // Checkmark
  const s = checkR;
  const lx = cx - s * 0.5;
  const ly = cy + s * 0.05;
  const mx = cx - s * 0.1;
  const my = cy + s * 0.45;
  const rx = cx + s * 0.55;
  const ry = cy - s * 0.4;

  // Curved text — "BLUEPAGES" top, "VERIFIED" bottom
  const topText = 'BLUEPAGES';
  const bottomText = 'VERIFIED';
  const fontSize = Math.max(size * 0.115, 5);
  const letterSpacing = size * 0.06;

  // Generate arc positions for top text
  const topChars = topText.split('');
  const topArc = topChars.map((ch, i) => {
    const fraction = (i - (topChars.length - 1) / 2) * (letterSize(topChars.length, letterSpacing, textR));
    const angle = -Math.PI / 2 + fraction / textR;
    return { ch, x: cx + textR * Math.cos(angle), y: cy + textR * Math.sin(angle), angle: angle + Math.PI / 2 };
  });

  // Generate arc positions for bottom text
  const botChars = bottomText.split('');
  const botArc = botChars.map((ch, i) => {
    const fraction = (i - (botChars.length - 1) / 2) * (letterSize(botChars.length, letterSpacing, textR));
    const angle = Math.PI / 2 + fraction / textR;
    return { ch, x: cx + textR * Math.cos(angle), y: cy + textR * Math.sin(angle), angle: angle - Math.PI / 2 };
  });

  return (
    <svg
      className={`verified-seal ${className}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Verified by BluePages"
      role="img"
    >
      {/* Outer ring */}
      <circle cx={cx} cy={cy} r={outerR} fill="none" stroke="#2563eb" strokeWidth={size * 0.035} />

      {/* Inner ring */}
      <circle cx={cx} cy={cy} r={ringR} fill="none" stroke="#2563eb" strokeWidth={size * 0.015} />

      {/* Thin decorative ring */}
      <circle cx={cx} cy={cy} r={innerR + size * 0.03} fill="none" stroke="#2563eb" strokeWidth={size * 0.008} opacity={0.35} />

      {/* Top arc text */}
      {topArc.map((t, i) => (
        <text
          key={`t-${i}`}
          x={t.x}
          y={t.y}
          fontSize={fontSize}
          fontWeight={600}
          fill="#2563eb"
          textAnchor="middle"
          dominantBaseline="central"
          transform={`rotate(${(t.angle * 180) / Math.PI}, ${t.x}, ${t.y})`}
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif', letterSpacing: '0.08em' }}
        >
          {t.ch}
        </text>
      ))}

      {/* Bottom arc text */}
      {botArc.map((t, i) => (
        <text
          key={`b-${i}`}
          x={t.x}
          y={t.y}
          fontSize={fontSize}
          fontWeight={700}
          fill="#2563eb"
          textAnchor="middle"
          dominantBaseline="central"
          transform={`rotate(${(t.angle * 180) / Math.PI}, ${t.x}, ${t.y})`}
          style={{ fontFamily: 'system-ui, -apple-system, sans-serif', letterSpacing: '0.12em' }}
        >
          {t.ch}
        </text>
      ))}

      {/* Center checkmark — thin, precise */}
      <path
        d={`M ${lx} ${ly} L ${mx} ${my} L ${rx} ${ry}`}
        fill="none"
        stroke="#2563eb"
        strokeWidth={size * 0.045}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function letterSize(_count: number, spacing: number, _r: number): number {
  return spacing / _r;
}
